import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Bot, Trash2, Copy, Check, X } from "lucide-react";

export default function MCPSettings() {
  const [keys, setKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/mcp/keys");
      setKeys(data || []);
    } catch {
      toast.error("Failed to load MCP keys");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mcpUrl = `${window.location.origin}/api/mcp`;

  const create = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/mcp/keys", { name: name.trim() });
      setNewKey(data);
      setShowModal(false);
      setName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create key");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"? Anything using it will stop working immediately.`)) return;
    try {
      await api.delete(`/mcp/keys/${k.key_id}`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke key");
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <DarkPage><div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">MCP Server</h1>
          <p className="text-sm text-[#7d8590] mt-1">Connect Claude Desktop or any MCP-compatible AI agent to this workspace's projects, tasks, invoices, and quotes.</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Key
        </Button>
      </div>

      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 mb-6">
        <div className="text-xs text-[#7d8590] mb-1">Server URL</div>
        <code className="text-sm text-[#00F5D4] font-mono">{mcpUrl}</code>
        <p className="text-xs text-[#7d8590] mt-2">
          Add this as a "Streamable HTTP" MCP server with an <code className="text-white">Authorization: Bearer &lt;key&gt;</code> header.
          Exposes read tools for projects, tasks, invoices, quotes and search, plus <code className="text-white">create_task</code> and <code className="text-white">update_task_status</code>.
        </p>
      </div>

      {keys.length === 0 ? (
        <div className="text-center py-16 text-[#7d8590] text-sm">No MCP keys yet. Create one to connect an external AI agent.</div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Name", "Key", "Last used", "Status", ""].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.key_id} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-3 text-white flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-[#7d8590]" /> {k.name}</td>
                  <td className="px-4 py-3 text-[#7d8590] font-mono">…{k.key_preview}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "Never"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${k.revoked ? "text-rose-400 bg-rose-500/10 border-rose-500/20" : "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20"}`}>
                      {k.revoked ? "Revoked" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!k.revoked && (
                      <button onClick={() => revoke(k)} className="p-1.5 text-[#7d8590] hover:text-rose-400" title="Revoke"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New MCP Key</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <label className="text-xs text-[#7d8590] mb-1 block">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Claude Desktop"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm mb-5" />
            <div className="flex gap-2">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create key"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* One-time reveal */}
      {newKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <h2 className="text-white font-semibold mb-2">Key created</h2>
            <p className="text-xs text-[#7d8590] mb-4">Copy this now — you won't be able to see it again.</p>
            <div className="flex items-center gap-2 bg-[#161b22] border border-[#30363d] rounded-md px-3 py-2 mb-5">
              <code className="text-xs text-[#00F5D4] font-mono flex-1 break-all">{newKey.key}</code>
              <button onClick={copyKey} className="text-[#7d8590] hover:text-white flex-shrink-0">
                {copied ? <Check className="w-4 h-4 text-[#00F5D4]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <Button onClick={() => setNewKey(null)} className="w-full bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              Done
            </Button>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
